import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { firstValueFrom, Observable, throwError } from 'rxjs';
import { TestedParty } from './tested-party.model';
import { LoggingService } from '@services/logging.service';

export const TESTED_PARTY_URL = '/api/AdjustmentsTestedParty';

@Injectable({
  providedIn: 'root',
})
export class TestedPartyService {
  constructor(private logger: LoggingService, private http: HttpClient) {}

  getBySearchStrategyId(searchStrategyId: number): Observable<TestedParty[]> {
    const url = TESTED_PARTY_URL + '/studySearchStrategyId/' + searchStrategyId;
    return this.http.get<TestedParty[]>(url).pipe(
      catchError((error) => {
        this.logger.httpError(this.constructor.name, url, error);
        return throwError(() => error);
      })
    );
  }

  getBySearchStrategyIdWithPaging(searchStrategyId: number, pageIndex: number, recordsPerPage: number): Observable<TestedParty[]> {
    const url = TESTED_PARTY_URL + '/studySearchStrategyId/' + searchStrategyId + '/' + pageIndex + '/' + recordsPerPage;
    return this.http.get<TestedParty[]>(url).pipe(
      catchError((error) => {
        this.logger.httpError(this.constructor.name, url, error);
        return throwError(() => error);
      })
    );
  }

  getTestedPartyCountBySearchStrategyId(searchStrategyId: number): Observable<number> {
    const url = TESTED_PARTY_URL + '/GetTestedPartyCountBySearchStrategyId/' + searchStrategyId;
    return this.http.get<number>(url).pipe(
      catchError((error) => {
        this.logger.httpError(this.constructor.name, url, error);
        return throwError(() => error);
      })
    );
  }

  async isTestedPartyPresentForSearchStrategy(searchStrategyId: number): Promise<boolean> {
    const url = TESTED_PARTY_URL + '/IsTestedPartyPresentForSearchStrategy/' + searchStrategyId;
    const result = <boolean>await firstValueFrom(this.http.get<boolean>(url)).catch((error) => {
      this.logger.httpError(this.constructor.name, TESTED_PARTY_URL, error);
    });
    return result;
  }

  updateInsert(testedParty: TestedParty): Observable<any> {
    if (testedParty.adjustmentsTestedPartyId) {
      return this.http.put(TESTED_PARTY_URL, testedParty).pipe(
        catchError((error) => {
          this.logger.httpError(this.constructor.name, TESTED_PARTY_URL, error);
          return throwError(() => error);
        })
      );
    }

    return this.http.post(TESTED_PARTY_URL, testedParty).pipe(
      catchError((error) => {
        this.logger.httpError(this.constructor.name, TESTED_PARTY_URL, error);
        return throwError(() => error);
      })
    );
  }

  getCompletedTestedParties(searchStrategyId: number): Observable<TestedParty[]> {
    const url = TESTED_PARTY_URL + '/getCompletedTestedParties/' + searchStrategyId;
    return this.http.get<TestedParty[]>(url).pipe(
      catchError((error) => {
        this.logger.httpError(this.constructor.name, url, error);
        return throwError(() => error);
      })
    );
  }

  async getCompletedTestedPartiesCount(searchStrategyId: number): Promise<number> {
    const url = TESTED_PARTY_URL + '/getCompletedTestedPartiesCount/' + searchStrategyId;
    const result = <number>await firstValueFrom(this.http.get<number>(url)).catch((error) => {
      this.logger.httpError(this.constructor.name, TESTED_PARTY_URL, error);
    });
    return result;
  }

  getTestedPartiesCompletedCount(searchStrategyId: number): Observable<number> {
    const url = TESTED_PARTY_URL + '/getCompletedTestedPartiesCount/' + searchStrategyId;
    return this.http.get<number>(url).pipe(
      catchError((error) => {
        this.logger.httpError(this.constructor.name, url, error);
        return throwError(() => error);
      })
    );
  }

  deleteTestedParty(searchStrategyId: number, testedPartyId: number): Observable<void> {
    const url = TESTED_PARTY_URL + '/deleteTestedParty/';
    return this.http.post<void>(url, { searchStrategyId, testedPartyId }).pipe(
      catchError((error) => {
        this.logger.httpError(this.constructor.name, url, error);
        return throwError(() => error);
      })
    );
  }
}
